import React, { createContext, useContext, useEffect, useState } from 'react';
// material
import { AppBar, Container, Dialog, Grid, IconButton, Toolbar, Typography, Stack, Box } from '@mui/material';
// components
import Page from '../components/Page';
import axios from "axios";
import AnnonceSelector from 'src/components/FormAnnonceList/AnnonceSelector';
import CloseIcon from '@mui/icons-material/Close';
import AnnonceDetails from 'src/sections/@dashboard/products/AnnonceDetails';
import { useParams } from 'react-router-dom';
import ShopProductCard from 'src/sections/@dashboard/products/ProductCard';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getCategories } from 'src/service/categoriesApi';
import AuthContext from 'src/contexts/AuthContext';
import { keyframes } from '@mui/system';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';

// Animation de la loupe
const rotate = keyframes`
  from {
    transform: rotate(0deg) translateX(20px) rotate(0deg);
  }
  to {
    transform: rotate(360deg) translateX(20px) rotate(-360deg);
  }
`;

const LoadingContainer = styled(Box)({
  position: 'relative',
  width: '200px',
  height: '200px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const StaticLogo = styled('img')({
  width: '150px',
  height: '150px',
  filter: 'drop-shadow(0 0 8px rgba(0, 90, 160, 0.3))',
});

const RotatingLoupe = styled(SearchIcon)({
  position: 'absolute',
  color: '#0058a7',
  fontSize: '40px',
  animation: `${rotate} 3s linear infinite`,
  filter: 'drop-shadow(0 0 4px rgba(0, 90, 160, 0.2))',
  right: '20px',
  top: '50%',
  transform: 'translateY(-50%)',
});

const SmallLoadingContainer = styled(Box)({
  position: 'relative',
  width: '100px',
  height: '100px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const SmallStaticLogo = styled('img')({
  width: '75px',
  height: '75px',
  filter: 'drop-shadow(0 0 8px rgba(0, 90, 160, 0.3))',
});

const SmallRotatingLoupe = styled(SearchIcon)({
  position: 'absolute',
  color: '#0058a7',
  fontSize: '20px',
  animation: `${rotate} 3s linear infinite`,
  filter: 'drop-shadow(0 0 4px rgba(0, 90, 160, 0.2))',
  right: '10px',
  top: '50%',
  transform: 'translateY(-50%)',
});

// ----------------------------------------------------------------------
const initialState = {
    selectedCategory: null,
    setSelectedCategory: () => undefined,
    filteredAttributes: null,
    setFilteredAttributes: () => undefined,
    date: null,
    setDate: () => undefined,
    numObject: null,
    setNumObject: () => undefined,
    email: null,
    setEmail: () => undefined,
    name: null,
    setName: () => undefined,
    globalValue: null,
    setGlobalValue: () => undefined,
};
export const FoundContext = createContext(initialState);

// Créer une instance d'axios avec les headers par défaut
const api = axios.create({
    baseURL: process.env.REACT_APP_PUBLIC_API || 'http://localhost:8000/api',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

export default function EcommerceShopLost() {
  const { customerId } = useContext(AuthContext);
  const { annonceID } = useParams() || null;

  // States
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [foundAds, setFoundAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [resultFilter, setResultFilter] = useState([]);
  const [count, setCount] = useState({ prev: 0, next: 12 });
  const [current, setCurrent] = useState([]);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredAttributes, setFilteredAttributes] = useState([]);
  const [date, setDate] = useState(null);
  const [numObject, setNumObject] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [globalValue, setGlobalValue] = useState('');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Construire l'URL avec les paramètres
        const params = new URLSearchParams({
          status: 'PERDU'
        });
        
        if (customerId) {
          params.append('professionalId', customerId);
        }
        
        // Faire les requêtes en parallèle
        const [categoriesResp, annoncesResp] = await Promise.all([
          getCategories(),
          api.get(`/annonces?${params.toString()}`)
        ]);

        const annonces = annoncesResp.data;

        // Mettre à jour les états
        setCategories(categoriesResp);
        setFoundAds(Array.isArray(annonces) ? annonces : []);
        setResultFilter(Array.isArray(annonces) ? annonces : []);

        if (annonceID) {
          makeFilter(Array.isArray(annonces) ? annonces : []);
        } else {
          const initialSlice = Array.isArray(annonces) ? annonces.slice(count.prev, count.next) : [];
          setCurrent(initialSlice);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setCategories([]);
        setFoundAds([]);
        setResultFilter([]);
        setCurrent([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [customerId, annonceID]);

  // Infinite scroll
  const haveMore = () => {
    if (current.length >= resultFilter.length) {
      setHasMore(false);
      return;
    }

    const nextBatch = resultFilter.slice(count.prev + 12, count.next + 12);
    setCurrent(prev => [...prev, ...nextBatch]);
    setCount(prev => ({
      prev: prev.prev + 12,
      next: prev.next + 12,
    }));
  };

  // Dialog handlers
  const handleClickOpenDialog = (data) => {
    setSelectedAnnonce(data);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Filter logic
  const makeFilter = (data = foundAds) => {
    let filteredData = [...data];

    // Email filter
    if (email) {
      filteredData = filteredData.filter((a) => 
        a.conversations && a.conversations.some((c) => 
          c.author && c.author.email && c.author.email.toLowerCase() === email.toLowerCase()
        )
      );
    }

    // Name filter
    if (name) {
      filteredData = filteredData.filter((a) => 
        a.annonceAttributes && a.annonceAttributes.some((c) => 
          c.isSecret && c.attributeValue && c.attributeValue.toLowerCase() === name.toLowerCase()
        )
      );
    }

    // Object number and category filter
    filteredData = filteredData.filter((a) => {
      if (numObject) return a.id === parseInt(numObject);
      if (annonceID) return a.id === parseInt(annonceID);
      if (selectedCategory) return a.category && a.category.id === selectedCategory;
      return true;
    });

    // Date filter
    if (date) {
      filteredData = filteredData.filter((a) => {
        const adDate = new Date(a.date);
        const filterDate = new Date(date);
        return adDate.setHours(0, 0, 0, 0) === filterDate.setHours(0, 0, 0, 0);
      });
    }

    // Attributes filter
    if (filteredAttributes.length > 0) {
      filteredData = filteredData.filter((a) =>
        filteredAttributes.every((attribute) =>
          a.annonceAttributes && a.annonceAttributes.some((attr) =>
            attribute.attributeValue !== -1
              ? attr.attribute === attribute.attribute &&
                attr.attributeValue && attr.attributeValue.toLowerCase() === attribute.attributeValue.toLowerCase()
              : attr.attribute != null
          )
        )
      );
    }

    // Global search filter
    if (globalValue) {
      const searchTerms = globalValue.toLowerCase().split(' ');
      filteredData = filteredData.filter((a) =>
        searchTerms.every((term) =>
          (a.category && a.category.name && a.category.name.toLowerCase().includes(term)) ||
          (a.annonceAttributes && a.annonceAttributes.some((attr) => 
            attr.attributeValue && attr.attributeValue.toLowerCase().includes(term)
          ))
        )
      );
    }

    setResultFilter(filteredData);
    setCurrent(filteredData.slice(count.prev, count.next));
  };

  // Update ads
  const updateUserAds = (data) => {
    setFoundAds(prevAds => 
      prevAds.map(ad => ad.id === data.id ? data : ad)
    );
    setCurrent(prevAds => 
      prevAds.map(ad => ad.id === data.id ? data : ad)
    );
  };

  // Apply filters when filter states change
  useEffect(() => {
    makeFilter();
  }, [selectedCategory, filteredAttributes, date, numObject, name, email, globalValue]);

  return (
    <Page title="Dashboard: Annonces">
      <FoundContext.Provider
        value={{
          selectedCategory,
          setSelectedCategory,
          filteredAttributes,
          setFilteredAttributes,
          date,
          setDate,
          numObject,
          setNumObject,
          name,
          setName,
          email,
          setEmail,
          globalValue,
          setGlobalValue,
        }}
      >
        <Container>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h4" component="h1">
                Objets Perdus
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" component="div">
                {resultFilter.length} Résultats
              </Typography>
            </Stack>

            <Box component="div">
              <AnnonceSelector />
            </Box>
            
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <LoadingContainer>
                  <StaticLogo src="/static/logo.svg" alt="Loading..." />
                  <RotatingLoupe />
                </LoadingContainer>
              </Box>
            ) : current.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Aucun objet perdu trouvé
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                  {resultFilter.length === 0 
                    ? "Il n'y a actuellement aucun objet perdu correspondant à vos critères de recherche." 
                    : "Essayez de modifier vos filtres pour voir plus de résultats."
                  }
                </Typography>
              </Box>
            ) : (
              <InfiniteScroll
                dataLength={current.length}
                next={haveMore}
                hasMore={hasMore}
                loader={
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <SmallLoadingContainer>
                      <SmallStaticLogo src="/static/logo.svg" alt="Loading..." />
                      <SmallRotatingLoupe />
                    </SmallLoadingContainer>
                  </Box>
                }
                endMessage={
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary" component="div">
                      Vous avez atteint la fin de la liste
                    </Typography>
                  </Box>
                }
                scrollThreshold="90%"
              >
                <Grid container spacing={3}>
                  {current.map((ads) => (
                    <Grid key={ads.id} item xs={12} sm={6} md={4}>
                      {ads && ads.conversations && ads.conversations[0]?.author && (
                        <ShopProductCard
                          ads={ads}
                          handleClickOpenDialog={handleClickOpenDialog}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </InfiniteScroll>
            )}
          </Stack>
        </Container>

        <Dialog 
          fullScreen 
          open={openDialog} 
          onClose={handleCloseDialog}
          TransitionProps={{
            onExited: () => setSelectedAnnonce(null)
          }}
        >
          <AppBar sx={{ position: 'relative', backgroundColor: 'transparent' }}>
            <Toolbar>
              <IconButton
                edge="start"
                onClick={handleCloseDialog}
                aria-label="close"
                sx={{ color: '#1B3038' }}
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {selectedAnnonce && (
            <AnnonceDetails 
              selectedAnnonce={selectedAnnonce} 
              setSelectedAnnonce={setSelectedAnnonce} 
              categories={categories} 
              updateUserAds={updateUserAds}
            />
          )}
        </Dialog>
      </FoundContext.Provider>
    </Page>
  );
}
